import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiArrowUpRight, FiClock } from 'react-icons/fi';
import { SERVER_BASE_URL, getImageUrl } from '../utils/api';
import { getPersonaLabel } from '../utils/expertRef';
import { expertTheme, focusRing, reducedMotion } from '../theme/expertTheme';

const Page = styled.div`
  min-height: 100vh;
  padding: clamp(48px, 7vw, 92px) 0 112px;
  background: radial-gradient(circle at 8% 2%, rgba(240,100,50,.1), transparent 27rem), ${expertTheme.colors.canvas};
  color: ${expertTheme.colors.ink};
`;

const Container = styled.div`
  width: min(${expertTheme.container}, calc(100% - 48px));
  margin: 0 auto;
  @media (max-width: 640px) { width: min(100% - 32px, ${expertTheme.container}); }
`;

const PageHeader = styled.header`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(280px, 430px);
  gap: 40px;
  align-items: end;
  margin-bottom: clamp(34px, 6vw, 68px);
  h1 { margin: 0; font-family: var(--font-body); font-size: clamp(3.2rem, 8vw, 7.4rem); line-height: .82; letter-spacing: -.065em; color: ${expertTheme.colors.ink}; }
  p { margin: 0; color: ${expertTheme.colors.inkSoft}; font-size: 17px; line-height: 1.65; }
  @media (max-width: 740px) { grid-template-columns: 1fr; gap: 24px; }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 24px;
  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;

const Card = styled(Link)`
  grid-column: ${p => p.$featured ? 'span 8' : 'span 4'};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: ${p => p.$featured ? '610px' : '480px'};
  border-radius: ${expertTheme.radius.large};
  background: ${p => p.$featured ? expertTheme.colors.charcoal : expertTheme.colors.surface};
  color: ${p => p.$featured ? '#fff' : expertTheme.colors.ink};
  box-shadow: 0 12px 40px rgba(42,38,30,.07);
  transition: transform 220ms ease, box-shadow 220ms ease;
  ${focusRing}
  ${reducedMotion}
  &:hover { transform: translateY(-6px); box-shadow: ${expertTheme.shadow.hover}; }
  @media (max-width: 1050px) { grid-column: span 6; }
  @media (max-width: 820px) { grid-column: auto; min-height: 0; }
`;

const Image = styled.div`
  min-height: ${p => p.$featured ? '360px' : '240px'};
  background: #dcd7cc center / cover no-repeat;
  @media (max-width: 620px) { min-height: 230px; }
`;

const CardBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: clamp(22px, 3vw, 34px);
`;

const Category = styled.span`
  align-self: flex-start;
  padding: 7px 11px;
  border-radius: ${expertTheme.radius.pill};
  background: ${p => p.$featured ? 'rgba(255,255,255,.13)' : expertTheme.colors.sageSoft};
  color: ${p => p.$featured ? '#f3bd9f' : expertTheme.colors.sage};
  font-size: 10px;
  font-weight: 850;
  letter-spacing: .1em;
  text-transform: uppercase;
`;

const Title = styled.h2`
  margin: 18px 0 12px;
  color: inherit;
  font-family: var(--font-body);
  font-size: ${p => p.$featured ? 'clamp(2rem, 4vw, 3.6rem)' : 'clamp(1.45rem, 2.3vw, 2rem)'};
  line-height: 1.08;
  letter-spacing: -.035em;
`;

const Excerpt = styled.p`
  margin: 0;
  color: ${p => p.$featured ? 'rgba(255,255,255,.68)' : expertTheme.colors.inkSoft};
  font-size: 14px;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Meta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-top: auto;
  padding-top: 28px;
  color: ${p => p.$featured ? 'rgba(255,255,255,.72)' : expertTheme.colors.inkSoft};
  font-size: 12px;
`;

const Author = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-width: 0;
  img, i { width: 34px; height: 34px; border-radius: 50%; object-fit: cover; object-position: top; background: ${expertTheme.colors.accent}; }
  i { display: grid; place-items: center; color: #fff; font-style: normal; font-weight: 800; }
  span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const ReadTime = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const State = styled.div`
  min-height: 430px;
  display: grid;
  place-items: center;
  text-align: center;
  p { color: ${expertTheme.colors.inkSoft}; }
`;

function getNewsImageUrl(path) {
  if (!path) return '/news_pic/1.png';
  if (path.startsWith('data:image/')) return path;
  return getImageUrl(path) || '/news_pic/1.png';
}

const News = () => {
  const [news, setNews] = useState([]);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${SERVER_BASE_URL}/api/news`, { signal: controller.signal })
      .then(response => response.json())
      .then(result => {
        if (!result.success) throw new Error('load-error');
        setNews(result.data || []);
        setStatus('success');
      })
      .catch(error => { if (error.name !== 'AbortError') setStatus('error'); });
    return () => controller.abort();
  }, []);

  if (status === 'loading') return <Page><Container><State><p>Собираем свежие материалы…</p></State></Container></Page>;
  if (status === 'error') return <Page><Container><State><p>Не удалось загрузить журнал. Попробуйте обновить страницу.</p></State></Container></Page>;

  return (
    <Page>
      <Container>
        <PageHeader><h1>Журнал</h1><p>Экспертные разборы, новости бренда и практические материалы о здоровье, питании и активной жизни.</p></PageHeader>
        {news.length ? <Grid>{news.map((item, index) => {
          const featured = index === 0;
          const authorName = item.expert_display_name || item.author;
          const initials = (authorName || 'L').split(' ').map(part => part[0]).join('').slice(0, 2);
          return <Card key={item.id} $featured={featured} to={item.slug ? `/news/${item.slug}` : `/news/${item.id}`}><Image $featured={featured} style={{ backgroundImage: `url(${getNewsImageUrl(item.main_image)})` }} /><CardBody><Category $featured={featured}>{item.category_name || item.category || 'Материал'}</Category><Title $featured={featured}>{item.title}</Title>{item.excerpt ? <Excerpt $featured={featured}>{item.excerpt}</Excerpt> : null}<Meta $featured={featured}><Author>{item.expert_photo_url ? <img src={getImageUrl(item.expert_photo_url)} alt="" /> : <i>{initials}</i>}<span>{authorName || 'Lagom Life'}{item.expert_persona_type ? ` · ${getPersonaLabel(item.expert_persona_type)}` : ''}</span></Author>{item.read_time ? <ReadTime><FiClock /> {item.read_time} мин.</ReadTime> : <FiArrowUpRight size={20} />}</Meta></CardBody></Card>;
        })}</Grid> : <State><p>Материалов пока нет.</p></State>}
      </Container>
    </Page>
  );
};

export default News;
